import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth, API } from "../App";
import { useLanguage } from "../i18n/LanguageContext";
import { useIsWebView } from "../hooks/useIsWebView";
import { useAvatar } from "../hooks/useAvatarContext";
import AvatarFigure from "./AvatarFigure";
import { X, Lock, Sparkles, TrendingUp, Heart, BookOpen, Bell, BellOff, Target, ChevronUp, AlertTriangle, Clock, Maximize2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";

const AvatarWidget = () => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const isWebView = useIsWebView();
  const navigate = useNavigate();
  const location = useLocation();
  const avatar = useAvatar();
  const [expanded, setExpanded] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [notifEnabled, setNotifEnabled] = useState(false);
  const [confetti, setConfetti] = useState(false);

  // Sound effects - singleton AudioContext (must be before early return)
  const playSound = useCallback((type) => {
    try {
      if (!window._futuraAudioCtx || window._futuraAudioCtx.state === "closed") {
        window._futuraAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = window._futuraAudioCtx;
      if (ctx.state === "suspended") ctx.resume();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.value = 0.15;
      if (type === "success") {
        osc.frequency.setValueAtTime(523, ctx.currentTime);
        osc.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
        osc.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        osc.start(); osc.stop(ctx.currentTime + 0.4);
      } else if (type === "levelup") {
        osc.frequency.setValueAtTime(440, ctx.currentTime);
        osc.frequency.setValueAtTime(554, ctx.currentTime + 0.1);
        osc.frequency.setValueAtTime(659, ctx.currentTime + 0.2);
        osc.frequency.setValueAtTime(880, ctx.currentTime + 0.3);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc.start(); osc.stop(ctx.currentTime + 0.5);
      } else if (type === "fail") {
        osc.type = "sawtooth";
        osc.frequency.setValueAtTime(300, ctx.currentTime);
        osc.frequency.setValueAtTime(200, ctx.currentTime + 0.15);
        gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc.start(); osc.stop(ctx.currentTime + 0.3);
      }
    } catch (e) { console.debug("[Sound]", e.message); }
  }, []);

  // Fetch avatar state on mount AND on every route change (auto-refresh)
  const refreshRef = useCallback(() => {
    if (user?.avatar_configured && avatar) {
      avatar.refreshAvatar();
    }
  }, [user?.avatar_configured, avatar]);

  useEffect(() => {
    refreshRef();
  }, [location.pathname, refreshRef]);

  // Check notification permission
  useEffect(() => {
    if ("Notification" in window) {
      setNotifEnabled(Notification.permission === "granted");
    }
  }, []);

  const [notifInstructionsOpen, setNotifInstructionsOpen] = useState(false);

  const handleEnableNotifications = async () => {
    if (!("Notification" in window)) { setNotifInstructionsOpen(true); return; }
    // In Android WebView, Notification.requestPermission() silently returns "denied" because
    // the OS-level permission must be granted from system Settings. Detect this case and show
    // the user actionable instructions instead of a silent failure.
    if (isWebView) { setNotifInstructionsOpen(true); return; }
    try {
      const permission = await Notification.requestPermission();
      setNotifEnabled(permission === "granted");
      if (permission === "granted") {
        new Notification("FUTURA", { body: t("avatar_notif_desc"), icon: "/futura-icon.svg" });
      } else if (permission === "denied") {
        // User explicitly blocked at browser level — show instructions to re-enable.
        setNotifInstructionsOpen(true);
      }
    } catch (err) {
      console.error("[Notifications]", err);
      setNotifInstructionsOpen(true);
    }
  };

  const handleCompleteChallenge = async () => {
    try {
      await axios.post(`${API}/avatar/challenge/complete`);
      if (avatar) avatar.setChallenge(prev => prev ? { ...prev, completed: true } : null);
      // Trigger confetti celebration + success sound
      playSound("success");
      setConfetti(true);
      setTimeout(() => setConfetti(false), 2500);
      // Refresh avatar state after challenge completion
      setTimeout(() => avatar?.refreshAvatar({ force: true }), 500);
    } catch (e) { console.debug("[Challenge]", e.message); }
  };

  const handleSkipChallenge = () => {
    // Hide the challenge card for the rest of the day (frontend-only skip).
    if (avatar) avatar.setChallenge(prev => prev ? { ...prev, skipped: true } : null);
    try {
      const today = new Date().toISOString().slice(0, 10);
      sessionStorage.setItem(`_ft_ch_skip_${today}`, "1");
    } catch (e) { console.debug("[SkipChallenge]", e.message); }
  };

  const handleRecoverStreak = async () => {
    try {
      await axios.post(`${API}/avatar/streak/recover`);
      playSound("success");
      setConfetti(true);
      setTimeout(() => setConfetti(false), 2500);
      setTimeout(() => avatar?.refreshAvatar({ force: true }), 300);
    } catch (e) { console.debug("[StreakRecover]", e.message); }
  };

  const avatarState = avatar?.avatarState;
  const challenge = avatar?.challenge;
  const state = avatarState?.state || "dormant";
  const score = avatarState?.clarity_score || 0;
  const color = avatarState?.avatar_color || user?.avatar_color || "#002FA7";

  // Sound on avatar state change (must be before early return)
  const prevStateRef = useRef(state);
  useEffect(() => {
    if (prevStateRef.current && prevStateRef.current !== state && user?.avatar_configured) {
      const stateOrder = { dormant: 0, awakening: 1, clear: 2, radiant: 3 };
      const prev = stateOrder[prevStateRef.current] ?? 0;
      const curr = stateOrder[state] ?? 0;
      if (curr > prev) playSound("levelup");
      else if (curr < prev) playSound("fail");
    }
    prevStateRef.current = state;
  }, [state, user?.avatar_configured, playSound]);

  // Public routes where the avatar widget must not appear, even if there's a cached user session.
  // Also hidden on /bienestar to reduce background fetches that were causing scroll lag on some WebView clients.
  const hiddenRoutes = ["/", "/login", "/register", "/verify-email", "/reset-password", "/pricing", "/terms", "/privacy", "/bienestar"];
  const isPublicRoute = hiddenRoutes.some(r => location.pathname === r || location.pathname.startsWith(r + "/"));

  if (!user || !user.avatar_configured || !avatar || isPublicRoute) return null;

  const metrics = avatarState?.metrics || {};
  const streak = avatarState?.streak || 0;
  const lastCheckin = avatarState?.last_checkin || null;
  const avatarLevel = avatar.level;
  const plan = user.plan || "descubre";
  const isAvanza = plan === "avanza";
  const challengeTextKey = challenge ? `avatar_challenge_${challenge.challenge_id}` : null;

  // Badge "needs check-in": aligned to UTC calendar-day reset (same as MoodTracker & challenges).
  // Appears whenever the user hasn't checked in on the current UTC calendar day.
  const needsCheckin = (() => {
    if (!lastCheckin) return true;
    try {
      const todayUTC = new Date().toISOString().slice(0, 10);
      return !String(lastCheckin).startsWith(todayUTC);
    } catch { return true; }
  })();

  return (
    <>
      {/* Confetti particles overlay */}
      {confetti && (
        <div className="fixed inset-0 z-[300] pointer-events-none" data-testid="avatar-confetti">
          {Array.from({ length: 30 }).map((_, i) => {
            const particleKey = `ft-confetti-${i}`;
            const dx = (i % 2 === 0 ? 1 : -1) * (50 + (i * 17 % 150));
            const dy = -(100 + (i * 31 % 200));
            const rot = 360 + (i * 47 % 360);
            return (
              <div
                key={particleKey}
                className="absolute rounded-full"
                style={{
                  left: `${40 + (i * 7 % 20)}%`,
                  top: `${60 + (i * 11 % 20)}%`,
                  width: 4 + (i % 6),
                  height: 4 + (i % 6),
                  backgroundColor: ["#FFD700", "#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD"][i % 7],
                  animation: `confetti-${i} ${1 + (i % 15) * 0.1}s ease-out forwards`,
                  animationDelay: `${(i % 10) * 0.03}s`,
                }}
              />
            );
          })}
          <style>{`
            ${Array.from({ length: 30 }).map((_, i) => {
              const dx = (i % 2 === 0 ? 1 : -1) * (50 + (i * 17 % 150));
              const dy = -(100 + (i * 31 % 200));
              const rot = 360 + (i * 47 % 360);
              return `@keyframes confetti-${i} { 0% { transform: translate(0, 0) scale(1) rotate(0deg); opacity: 1; } 100% { transform: translate(${dx}px, ${dy}px) scale(0) rotate(${rot}deg); opacity: 0; } }`;
            }).join('\n')}
          `}</style>
        </div>
      )}

      {/* Floating Avatar Button */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="fixed bottom-20 right-6 z-[100] rounded-full shadow-lg transition-all hover:scale-110 active:scale-95"
        style={{
          width: 60, height: 60,
          background: `radial-gradient(circle, ${color}40, ${color}15)`,
          border: `2px solid ${color}50`,
          backdropFilter: "blur(8px)",
        }}
        data-testid="avatar-widget-toggle"
        aria-label={t("avatar_widget_hint")}
        title={t("avatar_widget_hint")}
      >
        <AvatarFigure state={state} color={color} size={56} interactive={false} level={avatarLevel?.level || 0} />
        {needsCheckin && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E11D48] rounded-full flex items-center justify-center text-white text-[10px] font-bold border-2 border-white animate-pulse" data-testid="avatar-checkin-badge">!</span>
        )}
        {/* Persistent tap-hint below the avatar */}
        {!expanded && !fullscreen && (
          <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] font-semibold whitespace-nowrap pointer-events-none" style={{ color, textShadow: "0 1px 2px rgba(0,0,0,0.6)" }} data-testid="avatar-tap-hint">
            {t("avatar_tap_hint")}
          </span>
        )}
      </button>

      {/* Expanded Panel */}
      {expanded && !fullscreen && (
        <div className="fixed bottom-36 right-6 z-[101] w-80 max-h-[75vh] overflow-y-auto rounded-2xl shadow-2xl border animate-fade-in"
          style={{ background: "rgba(255,255,255,0.98)", borderColor: `${color}25` }}
          data-testid="avatar-widget-panel"
        >
          {/* Header */}
          <div className="p-4 border-b border-[#E5E5EA]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AvatarFigure state={state} color={color} size={40} interactive={false} />
                <div>
                  <p className="font-['Outfit'] font-semibold text-[#111111] text-sm">{t("avatar_widget_title")}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-[#6B7280]">{t(`avatar_state_${state}`)}</p>
                    {avatarLevel?.has_levels_feature && avatarLevel.level && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }} data-testid="avatar-level-badge">
                        {t("avatar_level")} {avatarLevel.level}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setFullscreen(true)}
                  className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 active:scale-95 transition-all shadow-sm"
                  style={{ backgroundColor: color, color: "#FFFFFF" }}
                  data-testid="avatar-fullscreen-btn"
                  title={t("avatar_fullscreen_hint")}
                  aria-label={t("avatar_fullscreen_hint")}
                >
                  <Maximize2 className="w-3.5 h-3.5" strokeWidth={2.5} />
                  <span>{t("avatar_fullscreen_btn")}</span>
                </button>
                <button onClick={() => setExpanded(false)} className="text-[#6B7280] hover:text-[#111111] p-1.5 hover:bg-[#F5F5F7] rounded-full transition-colors" data-testid="avatar-widget-close" aria-label="Cerrar">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Clarity Score */}
          <div className="p-4 pb-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-[#6B7280]">{t("avatar_clarity_score")}</span>
              <span className="text-sm font-semibold" style={{ color }}>{score}/100</span>
            </div>
            <div className="w-full h-2 bg-[#F5F5F7] rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${Math.max(score, 2)}%`, backgroundColor: color }} />
            </div>
            <p className="text-xs text-[#6B7280] mt-2">{t(`avatar_insight_${state}`)}</p>
          </div>

          {/* Streak */}
          {streak > 0 && (
            <div className="mx-4 mb-3 flex items-center gap-3 p-3 rounded-xl" style={{ background: `${color}08`, border: `1px solid ${color}12` }} data-testid="avatar-streak">
              <div className="text-2xl">
                {streak < 3 ? "\ud83d\udd25" : streak < 7 ? "\ud83d\udd25\ud83d\udd25" : "\ud83d\udd25\ud83d\udd25\ud83d\udd25"}
              </div>
              <div>
                <p className="font-semibold text-sm text-[#111111]">{streak} {t("avatar_streak")}</p>
                <p className="text-[10px] text-[#6B7280]">{t("avatar_streak_msg")}</p>
              </div>
            </div>
          )}

          {/* Streak Recovery (Decide/Avanza, 1x/month) */}
          {avatar?.streakStatus?.has_feature && (
            <div className="mx-4 mb-3 p-3 rounded-xl border" style={{ background: "#FEF3C7", borderColor: "#FCD34D" }} data-testid="streak-recovery-card">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-lg">{"\u2744\ufe0f"}</span>
                <span className="text-xs font-semibold text-[#111111]">{t("streak_recovery_title")}</span>
              </div>
              {avatar.streakStatus.can_recover ? (
                <>
                  <p className="text-[11px] text-[#6B7280] mb-2 leading-relaxed">{t("streak_recovery_available")}</p>
                  <button
                    onClick={handleRecoverStreak}
                    className="w-full text-[11px] font-medium px-3 py-2 rounded-full text-white bg-[#D97706] hover:bg-[#B45309] transition-colors"
                    data-testid="streak-recovery-apply-btn"
                  >
                    {t("streak_recovery_apply")}
                  </button>
                </>
              ) : avatar.streakStatus.used_this_month ? (
                <p className="text-[11px] text-[#6B7280] leading-relaxed" data-testid="streak-recovery-used">
                  {t("streak_recovery_used_month")}
                </p>
              ) : (
                <p className="text-[11px] text-[#6B7280] leading-relaxed" data-testid="streak-recovery-none">
                  {t("streak_recovery_no_gap")}
                </p>
              )}
            </div>
          )}

          {/* Timeline link */}
          <button onClick={() => { navigate("/timeline"); setExpanded(false); }}
            className="mx-4 mb-3 w-[calc(100%-2rem)] flex items-center gap-2.5 p-3 bg-[#F5F5F7] hover:bg-[#E5E5EA] rounded-xl transition-all text-left"
            data-testid="avatar-timeline-btn"
          >
            <Clock className="w-4 h-4" style={{ color }} />
            <div className="flex-1">
              <p className="text-xs font-medium text-[#111111]">{t("avatar_timeline_btn")}</p>
              <p className="text-[10px] text-[#6B7280]">{t("avatar_timeline_btn_desc")}</p>
            </div>
            <ChevronUp className="w-3.5 h-3.5 text-[#6B7280] rotate-90" />
          </button>

          {/* Micro-Challenge */}
          {challenge && !challenge.skipped && (() => {
            try {
              const todayStr = new Date().toISOString().slice(0, 10);
              if (sessionStorage.getItem(`_ft_ch_skip_${todayStr}`) === "1") return null;
            } catch { /* sessionStorage unavailable */ }
            return (
            <div className="mx-4 mb-3 p-3 rounded-xl" style={{ background: `${color}08`, border: `1px solid ${color}15` }} data-testid="avatar-challenge-card">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-3.5 h-3.5" style={{ color }} />
                <span className="text-xs font-semibold text-[#111111]">{t("avatar_challenge_title")}</span>
              </div>
              <p className="text-xs text-[#6B7280] mb-2.5">{challengeTextKey ? t(challengeTextKey) : ""}</p>
              {!challenge.completed ? (
                <div className="flex gap-2">
                  <button onClick={handleCompleteChallenge} className="text-[10px] font-medium px-3 py-1.5 rounded-full text-white" style={{ backgroundColor: color }} data-testid="avatar-challenge-accept">
                    {t("avatar_challenge_accept")}
                  </button>
                  <button onClick={handleSkipChallenge} className="text-[10px] text-[#6B7280] px-2 py-1.5 hover:text-[#111111] transition-colors" data-testid="avatar-challenge-skip">
                    {t("avatar_challenge_skip")}
                  </button>
                </div>
              ) : (
                <span className="text-[10px] font-medium px-3 py-1 rounded-full inline-block" style={{ backgroundColor: `${color}15`, color }}>{t("avatar_challenge_done")}</span>
              )}
            </div>
            );
          })()}

          {/* Activity Metrics */}
          <div className="px-4 pb-3 grid grid-cols-3 gap-2">
            <div className="bg-[#F5F5F7] rounded-xl p-2 text-center">
              <Heart className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color }} />
              <p className="text-base font-semibold text-[#111111]">{metrics.mood_checkins || 0}</p>
              <p className="text-[9px] text-[#6B7280]">{t("avatar_metric_checkins")}</p>
            </div>
            <div className="bg-[#F5F5F7] rounded-xl p-2 text-center">
              <TrendingUp className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color }} />
              <p className="text-base font-semibold text-[#111111]">{metrics.consultations || 0}</p>
              <p className="text-[9px] text-[#6B7280]">{t("avatar_metric_consults")}</p>
            </div>
            <div className="bg-[#F5F5F7] rounded-xl p-2 text-center">
              <BookOpen className="w-3.5 h-3.5 mx-auto mb-0.5" style={{ color }} />
              <p className="text-base font-semibold text-[#111111]">{metrics.diary_entries || 0}</p>
              <p className="text-[9px] text-[#6B7280]">{t("avatar_metric_diary")}</p>
            </div>
          </div>

          {/* Notifications toggle */}
          {"Notification" in window && (
            <div className="mx-4 mb-3 flex items-center justify-between p-2.5 bg-[#F5F5F7] rounded-xl">
              <div className="flex items-center gap-2">
                {notifEnabled ? <Bell className="w-3.5 h-3.5" style={{ color }} /> : <BellOff className="w-3.5 h-3.5 text-[#6B7280]" />}
                <span className="text-xs text-[#111111]">{notifEnabled ? t("avatar_notif_enabled") : t("avatar_notif_title")}</span>
              </div>
              {!notifEnabled && (
                <button onClick={handleEnableNotifications} className="text-[10px] font-medium px-2.5 py-1 rounded-full text-white" style={{ backgroundColor: color }} data-testid="avatar-notif-enable">
                  {t("avatar_notif_enable")}
                </button>
              )}
            </div>
          )}

          {/* Upsell / Insight */}
          {!isAvanza && (
            <div className="mx-4 mb-3 p-3 rounded-xl border border-dashed" style={{ borderColor: `${color}30`, background: `${color}05` }}>
              <div className="flex items-center gap-2 mb-1">
                <Lock className="w-3.5 h-3.5" style={{ color }} />
                <span className="text-xs font-semibold" style={{ color }}>{t("avatar_deep_analysis_title")}</span>
              </div>
              <p className="text-[10px] text-[#6B7280] mb-2">{t("avatar_deep_analysis_desc")}</p>
              {!isWebView && (
                <button onClick={() => { navigate("/pricing"); setExpanded(false); }} className="text-[10px] font-medium px-3 py-1.5 rounded-full text-white" style={{ backgroundColor: color }} data-testid="avatar-upgrade-btn">
                  {t("avatar_upgrade_cta")}
                </button>
              )}
            </div>
          )}
          {isAvanza && (
            <div className="mx-4 mb-3 p-3 rounded-xl" style={{ background: `${color}08`, border: `1px solid ${color}15` }}>
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-3.5 h-3.5" style={{ color }} />
                <span className="text-xs font-semibold" style={{ color }}>{t("avatar_deep_insight_title")}</span>
              </div>
              <p className="text-xs text-[#6B7280]">{t("avatar_deep_insight_desc")}</p>
            </div>
          )}

          {/* Ethical banner */}
          <div className="mx-4 mb-3 flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200/50 rounded-xl">
            <AlertTriangle className="w-3 h-3 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-[9px] text-amber-700 leading-relaxed">{t("avatar_ethical_banner")}</p>
          </div>
        </div>
      )}

      {/* Fullscreen Avatar Modal */}
      {fullscreen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-[#0a0a0f]/95 backdrop-blur-sm" data-testid="avatar-fullscreen-modal" onClick={() => setFullscreen(false)}>
          <div className="relative flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setFullscreen(false)} className="absolute top-4 right-4 text-white/50 hover:text-white z-10" data-testid="avatar-fullscreen-close">
              <X className="w-6 h-6" />
            </button>
            <div className="relative mb-6">
              <AvatarFigure state={state} color={color} size={320} interactive={true} level={avatarLevel?.level || 0} />
              <div className="absolute inset-0 -z-10 rounded-full blur-3xl opacity-30" style={{ backgroundColor: color }} />
            </div>
            <p className="font-['Outfit'] text-2xl font-semibold text-white mb-2">{t(`avatar_state_${state}`)}</p>
            <p className="text-white/50 text-sm mb-4">{t("avatar_clarity_score")}: {score}/100</p>
            <div className="flex gap-2 mb-6">
              {["dormant", "awakening", "clear", "radiant"].map((s) => (
                <span key={s} className={`text-xs px-3 py-1.5 rounded-full border ${state === s ? "border-white/30 bg-white/10 text-white" : "border-white/5 text-white/25"}`}>
                  {t(`avatar_state_${s}`)}
                </span>
              ))}
            </div>
            <p className="text-white/30 text-xs max-w-md text-center">{t("avatar_ethical_banner")}</p>
          </div>
        </div>
      )}

      {/* Notifications instructions modal (WebView / denied permission case) */}
      {notifInstructionsOpen && (
        <div className="fixed inset-0 z-[201] bg-black/80 flex items-center justify-center p-4 animate-fade-in" onClick={() => setNotifInstructionsOpen(false)} data-testid="avatar-notif-instructions">
          <div className="bg-white rounded-3xl p-6 max-w-sm w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
                <Bell className="w-5 h-5" style={{ color }} />
              </div>
              <h3 className="font-['Outfit'] font-semibold text-[#111111] text-lg">{t("avatar_notif_instructions_title")}</h3>
            </div>
            <p className="text-sm text-[#6B7280] mb-3 leading-relaxed">
              {isWebView ? t("avatar_notif_instructions_app") : t("avatar_notif_instructions_web")}
            </p>
            <button
              onClick={() => setNotifInstructionsOpen(false)}
              className="w-full text-sm font-semibold px-4 py-3 rounded-full text-white hover:opacity-90 transition-opacity"
              style={{ backgroundColor: color }}
              data-testid="avatar-notif-instructions-close"
            >
              {t("avatar_notif_instructions_ok")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default AvatarWidget;
